/**
 * allocationUtils.js
 * -------------------
 * Shared pure utility functions, date calculation helpers, color formatters,
 * and layout algorithms for Resource Allocation Board and Assignment Modals.
 */

// ─────────────────────────────────────────────────────────────────────────────
// Constants
// ─────────────────────────────────────────────────────────────────────────────
export const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
export const COL_W = 100          // px per day column in monthly/weekly Gantt
export const ROW_PAD = 12         // top/bottom padding inside each row
export const BLOCK_H = 40         // h-10 pill height
export const LANE_GAP = 52        // lane height = BLOCK_H + 12px gap
export const MIN_ROW_H = 160      // at least 2 lanes + button spacing
export const DAY_ROW_HEIGHT = 56

export const HOUR_LABELS = Array.from({ length: 24 }).map((_, hour) => {
  if (hour === 0) return '12 AM'
  if (hour < 12) return `${hour} AM`
  if (hour === 12) return '12 PM'
  return `${hour - 12} PM`
})

// ─────────────────────────────────────────────────────────────────────────────
// Date & Time Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function toDateKey(d) {
  if (!d) return ''
  const dateObj = typeof d === 'string' ? parseDate(d) : d
  if (!dateObj || isNaN(dateObj.getTime())) return ''
  const year = dateObj.getFullYear()
  const month = String(dateObj.getMonth() + 1).padStart(2, '0')
  const day = String(dateObj.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

export function parseDate(str) {
  if (!str) return null
  const d = new Date(str)
  return isNaN(d.getTime()) ? null : d
}

// Parse a naive datetime ISO string (no timezone info) as local time.
export function parseNaiveDate(iso) {
  if (!iso) return new Date(NaN)
  const match = String(iso).match(/^(\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}:\d{2})/)
  if (match) {
    return new Date(match[1].replace(' ', 'T'))
  }
  return new Date(iso)
}

export function formatClock(iso) {
  if (!iso) return '--'
  const date = parseNaiveDate(iso)
  return date.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })
}

export function toHHMM(hour, minute = 0) {
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

export function toTimeLabel(hour, minute = 0) {
  const period = hour >= 12 ? 'PM' : 'AM'
  const normalized = hour % 12 || 12
  if (minute === 0) return `${normalized} ${period}`
  return `${normalized}:${String(minute).padStart(2, '0')} ${period}`
}

export function getWeekStartMonday(date) {
  const start = new Date(date)
  start.setHours(0, 0, 0, 0)
  const day = start.getDay()
  const diff = start.getDate() - day + (day === 0 ? -6 : 1)
  start.setDate(diff)
  return start
}

export function buildWeekDays(weekStart) {
  return Array.from({ length: 7 }, (_, i) => {
    const d = new Date(weekStart)
    d.setDate(weekStart.getDate() + i)
    return { dayNum: d.getDate(), weekday: WEEKDAYS[d.getDay()], date: d }
  })
}

export function formatWeekLabel(weekStart) {
  const end = new Date(weekStart)
  end.setDate(weekStart.getDate() + 6)
  const startStr = weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })
  const endStr = end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
  return `${startStr} – ${endStr}`
}

export function formatMonthLabel(year, month) {
  return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
    month: 'long',
    year: 'numeric',
  })
}

export function buildMonthDays(year, month) {
  const daysInMonth = new Date(year, month, 0).getDate()
  return Array.from({ length: daysInMonth }, (_, i) => {
    const d = new Date(year, month - 1, i + 1)
    return { dayNum: i + 1, weekday: WEEKDAYS[d.getDay()], date: d }
  })
}

export function buildCalendarMatrix(year, month) {
  const firstDay = new Date(year, month - 1, 1)
  const daysInMonth = new Date(year, month, 0).getDate()
  const startOffset = firstDay.getDay()
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7
  const cells = []
  for (let i = 0; i < totalCells; i++) {
    const dayNum = i - startOffset + 1
    if (dayNum < 1 || dayNum > daysInMonth) {
      cells.push(null)
    } else {
      const d = new Date(year, month - 1, dayNum)
      cells.push({ dayNum, date: d, key: toDateKey(d) })
    }
  }
  return cells
}

export function getCurrentMonth() {
  const now = new Date()
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
}

// ─────────────────────────────────────────────────────────────────────────────
// Priority & Status Color Helpers
// ─────────────────────────────────────────────────────────────────────────────
export function priorityBadgeColor(priority) {
  if (!priority) return 'bg-surface-variant text-outline'
  const p = String(priority).toUpperCase()
  if (p === 'HIGH' || p === 'URGENT' || p === 'CRITICAL') return 'bg-red-50 border border-error text-error'
  if (p === 'MEDIUM') return 'bg-primary text-white'
  if (p === 'LOW') return 'bg-surface-variant text-outline'
  return 'bg-secondary-container text-on-secondary-container'
}

export function getTaskPriorityColorClass(priority) {
  if (!priority) return 'bg-primary border-2 border-primary text-white'
  const p = String(priority).toUpperCase()
  if (p === 'HIGH' || p === 'URGENT' || p === 'CRITICAL') return 'bg-red-50 border-2 border-error text-error'
  if (p === 'MEDIUM') return 'bg-primary border-2 border-primary text-white'
  if (p === 'LOW') return 'bg-surface-variant border-2 border-outline-variant/20'
  return 'bg-primary border-2 border-primary text-white'
}

export function getSuggestedSlotColorClass(priority) {
  if (!priority) return 'bg-white border-primary text-primary hover:bg-blue-50/30'
  const p = String(priority).toUpperCase()
  if (p === 'HIGH' || p === 'URGENT' || p === 'CRITICAL') return 'bg-white border-error text-error hover:bg-red-50/30'
  if (p === 'MEDIUM') return 'bg-white border-primary text-primary hover:bg-blue-50/30'
  if (p === 'LOW') return 'bg-white border-outline text-outline hover:bg-surface-variant/30'
  return 'bg-white border-primary text-primary hover:bg-blue-50/30'
}

export function getJobStatusColorClass(status) {
  if (!status) return 'bg-primary border-2 border-primary text-white'
  const s = String(status).toUpperCase()
  if (s === 'COMPLETED') return 'bg-emerald-600 border-2 border-emerald-700 text-white'
  if (s === 'IN_PROGRESS' || s === 'IN PROGRESS') return 'bg-blue-600 border-2 border-blue-700 text-white'
  if (s === 'ON_HOLD' || s === 'ON HOLD') return 'bg-amber-500 border-2 border-amber-600 text-white'
  if (s === 'PENDING_REVIEW' || s === 'PENDING REVIEW') return 'bg-purple-600 border-2 border-purple-700 text-white'
  if (s === 'CLOSED') return 'bg-slate-500 border-2 border-slate-600 text-white'
  return 'bg-primary border-2 border-primary text-white'
}

// ─────────────────────────────────────────────────────────────────────────────
// Suggested Slot Forward & Backward Scans
// ─────────────────────────────────────────────────────────────────────────────
export function findSuggestedRange(candidates, effort, minHours = 0) {
  const filtered = candidates.filter((d) => parseFloat(d.free_hours) >= minHours)
  if (filtered.length === 0) return { success: false }
  let sum = 0
  for (let i = 0; i < filtered.length; i++) {
    sum += parseFloat(filtered[i].free_hours)
    if (sum >= effort) {
      return { start: filtered[0].date, end: filtered[i].date, success: true }
    }
  }
  return { success: false }
}

export function findSuggestedRangeBackward(candidates, effort, minHours = 0) {
  const filtered = candidates.filter((d) => parseFloat(d.free_hours) >= minHours)
  if (filtered.length === 0) return { success: false }
  let sum = 0
  for (let i = filtered.length - 1; i >= 0; i--) {
    sum += parseFloat(filtered[i].free_hours)
    if (sum >= effort) {
      return { start: filtered[i].date, end: filtered[filtered.length - 1].date, success: true }
    }
  }
  return { success: false }
}

// ─────────────────────────────────────────────────────────────────────────────
// Gantt Positioning & Lane Packing
// ─────────────────────────────────────────────────────────────────────────────
export function blockStyle(timelineStart, timelineEnd, start, end) {
  const s = parseDate(start)
  const e = parseDate(end) || parseDate(start)
  if (!s || !e || !timelineStart || !timelineEnd) return null

  const clampedStart = s < timelineStart ? timelineStart : s
  const clampedEnd = e > timelineEnd ? timelineEnd : e
  if (clampedStart > clampedEnd) return null

  const msPerDay = 1000 * 60 * 60 * 24
  const tStartMidnight = new Date(timelineStart.getFullYear(), timelineStart.getMonth(), timelineStart.getDate())
  const clampedStartMidnight = new Date(clampedStart.getFullYear(), clampedStart.getMonth(), clampedStart.getDate())
  const clampedEndMidnight = new Date(clampedEnd.getFullYear(), clampedEnd.getMonth(), clampedEnd.getDate())

  const startIdx = Math.round((clampedStartMidnight - tStartMidnight) / msPerDay)
  const spanDays = Math.max(1, Math.round((clampedEndMidnight - clampedStartMidnight) / msPerDay) + 1)

  return {
    left: `${startIdx * COL_W + 6}px`,
    width: `${spanDays * COL_W - 12}px`,
  }
}

export function assignLanes(rawBlocks) {
  const sorted = [...rawBlocks].sort((a, b) => {
    if (!a.start && !b.start) return 0
    if (!a.start) return 1
    if (!b.start) return -1
    return a.start - b.start
  })

  const laneEnds = []

  const withLane = sorted.map((block) => {
    const s = block.start
    const e = block.end

    let placed = -1
    for (let l = 0; l < laneEnds.length; l++) {
      if (!s || !laneEnds[l] || s > laneEnds[l]) {
        placed = l
        break
      }
    }
    if (placed === -1) {
      placed = laneEnds.length
      laneEnds.push(null)
    }
    if (e) laneEnds[placed] = !laneEnds[placed] || e > laneEnds[placed] ? e : laneEnds[placed]

    return { ...block, lane: placed }
  })

  return { lanes: Math.max(2, laneEnds.length), blocks: withLane }
}

// ─────────────────────────────────────────────────────────────────────────────
// Daily Timeline Constants & Helpers (Real Availability / Jobs Mode)
// ─────────────────────────────────────────────────────────────────────────────
export const DAY_START_HOUR = 0      // 12 AM (Midnight)
export const DAY_END_HOUR   = 24     // 12 AM Next Day (Full 24-hour timeline: 12 AM to 12 PM to 12 AM)
export const HOUR_SLOT_W    = 110    // px per hour slot on daily Gantt
export const DAILY_BLOCK_H  = 40     // pill height

/**
 * Build array of hour-slot objects for a day view.
 * @returns [{hour: 9, label: '9 AM'}, {hour: 10, label: '10 AM'}, ...]
 */
export function buildDayHours(startHour = DAY_START_HOUR, endHour = DAY_END_HOUR) {
  return Array.from(
    { length: endHour - startHour },
    (_, i) => ({ hour: startHour + i, label: toTimeLabel(startHour + i) })
  )
}

/**
 * Given a job_block with planned_start/planned_end ISO strings,
 * compute left offset (px) and width (px) on the daily timeline.
 */
export function dailyBlockStyle(dateStr, startIso, endIso, plannedHours = null, shiftIntime = null) {
  if (!startIso) return null
  const startDate = parseNaiveDate(startIso)
  let endDate = endIso ? parseNaiveDate(endIso) : null
  if (isNaN(startDate.getTime())) return null

  const jobStartDateStr = toDateKey(startDate)
  const jobEndDateStr = endDate && !isNaN(endDate.getTime()) ? toDateKey(endDate) : jobStartDateStr

  // If the job does not overlap with dateStr at all, return null
  if (jobEndDateStr < dateStr || jobStartDateStr > dateStr) return null

  // Calculate start hour within dateStr
  let startH = DAY_START_HOUR
  let isDateOnly = false
  if (jobStartDateStr === dateStr) {
    const h = startDate.getHours()
    const m = startDate.getMinutes()
    const endHRaw = endDate && !isNaN(endDate.getTime()) ? endDate.getHours() : 0
    const endMRaw = endDate && !isNaN(endDate.getTime()) ? endDate.getMinutes() : 0
    if (h === 0 && m === 0 && endHRaw === 0 && endMRaw === 0) {
      // Date-only job created without explicit time: place during employee's shift start (or 9 AM)
      isDateOnly = true
      const shiftMin = parseTimeToMinutes(shiftIntime)
      startH = shiftMin != null ? shiftMin / 60 : 9
    } else {
      startH = h + m / 60
    }
  } else if (jobStartDateStr < dateStr) {
    startH = DAY_START_HOUR
  }

  // Calculate end hour within dateStr
  let endH = DAY_END_HOUR
  if (isDateOnly) {
    const span = plannedHours ? Math.min(8, Number(plannedHours)) : 4
    endH = startH + span
  } else if (jobEndDateStr === dateStr && endDate && !isNaN(endDate.getTime())) {
    endH = endDate.getHours() + endDate.getMinutes() / 60
    if (endH <= startH) {
      const span = plannedHours ? Number(plannedHours) : 1
      endH = startH + span
    }
  } else if (jobEndDateStr > dateStr) {
    endH = DAY_END_HOUR
  } else {
    const span = plannedHours ? Number(plannedHours) : 1
    endH = startH + span
  }

  // Minimum duration of 30 mins for visibility
  if (endH <= startH) {
    endH = startH + 0.5
  }

  const clampedStart = Math.max(startH, DAY_START_HOUR)
  const clampedEnd = Math.min(endH, DAY_END_HOUR)
  if (clampedStart >= clampedEnd) return null

  return {
    left: `${(clampedStart - DAY_START_HOUR) * HOUR_SLOT_W + 6}px`,
    width: `${Math.max(36, (clampedEnd - clampedStart) * HOUR_SLOT_W - 12)}px`,
  }
}

/** Format a Date as 'Mon, Sep 29, 2026' */
export function formatDayLabel(date) {
  if (!date) return ''
  const d = typeof date === 'string' ? parseDate(date) : date
  if (!d || isNaN(d.getTime())) return ''
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })
}

/** Add N days to a Date, return new Date */
export function addDays(date, n) {
  const d = new Date(date)
  d.setDate(d.getDate() + n)
  return d
}

/**
 * For an employee's job_blocks, compute how many hours are allocated
 * in each hour slot of `dateStr`.
 * @returns number[] indexed by (hour - startHour)
 */
export function buildHourlyWorkload(empJobBlocks = [], dateStr, startHour = DAY_START_HOUR, endHour = DAY_END_HOUR) {
  const slots = new Array(endHour - startHour).fill(0)
  empJobBlocks.forEach((b) => {
    if (!b.planned_start) return
    const s = parseNaiveDate(b.planned_start)
    const e = b.planned_end ? parseNaiveDate(b.planned_end) : new Date(s.getTime() + 60 * 60 * 1000)
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return

    const sDate = toDateKey(s)
    const eDate = toDateKey(e)
    if (eDate < dateStr || sDate > dateStr) return

    const sH = sDate === dateStr ? (s.getHours() + s.getMinutes() / 60) : startHour
    const eH = eDate === dateStr ? (e.getHours() + e.getMinutes() / 60) : endHour

    for (let h = startHour; h < endHour; h++) {
      const slotS = h
      const slotE = h + 1
      const overlap = Math.min(slotE, eH) - Math.max(slotS, sH)
      if (overlap > 0) {
        slots[h - startHour] += overlap
      }
    }
  })
  return slots
}

/** Returns day capacity string e.g. '8h / day' */
export function getDayCapacityLabel(employee) {
  const cap = employee?.base_hours_per_day ?? employee?.daily_capacity_hours ?? employee?.capacity_hours ?? 8
  return `${Number(cap).toFixed(0)}h / day`
}

/** Next available date string for an employee from board data */
export function getNextAvailableDate(employee) {
  const dateStr = employee?.next_available_date ?? employee?.next_available ?? null
  if (!dateStr) return null
  return String(dateStr).slice(0, 10)
}

/**
 * Parse "HH:MM" or "HH:MM:SS" time string to minutes from midnight.
 * Returns null if invalid or missing.
 */
export function parseTimeToMinutes(timeStr) {
  if (!timeStr) return null
  const parts = String(timeStr).trim().split(':')
  if (parts.length >= 2) {
    const h = parseInt(parts[0], 10)
    const m = parseInt(parts[1], 10)
    if (!isNaN(h) && !isNaN(m)) return h * 60 + m
  }
  return null
}

/**
 * Computes available time intervals (free slots) for an employee on a specific date.
 * Considers ShiftMaster times (intime / outtime), daily availability cache (working/leave/holiday),
 * remaining free capacity, and existing job blocks.
 */
export function computeDayFreeSlots(emp, dateStr, dayJobs = [], startHour = DAY_START_HOUR, endHour = DAY_END_HOUR) {
  if (!emp || !dateStr) return []

  const baseHours = parseFloat(emp.base_hours_per_day || 8)

  // 1. Check if non-working day or depleted capacity from availability cache
  const dailyRows = emp.available_slot?.daily_rows || []
  const dayRow = dailyRows.find((r) => r.date === dateStr)
  if (dayRow) {
    const dayType = String(dayRow.day_type || 'WORKING').toUpperCase()
    if (dayType === 'WEEKEND' || dayType === 'HOLIDAY' || (dayType.startsWith('LEAVE') && parseFloat(dayRow.free_hours || 0) <= 0)) {
      return []
    }
  }

  // 2. Determine actual free productive capacity for this employee on this date
  const totalAllocatedJobHours = (dayJobs || []).reduce((sum, j) => {
    const ph = parseFloat(j.planned_hours)
    if (!isNaN(ph) && ph > 0) return sum + ph
    if (j.planned_start && j.planned_end) {
      const s = parseNaiveDate(j.planned_start)
      const e = parseNaiveDate(j.planned_end)
      if (!isNaN(s.getTime()) && !isNaN(e.getTime()) && e > s) {
        return sum + (e.getTime() - s.getTime()) / (1000 * 3600)
      }
    }
    return sum
  }, 0)

  const actualDayFreeHours = dayRow != null
    ? parseFloat(dayRow.free_hours ?? 0)
    : Math.max(0, baseHours - totalAllocatedJobHours)

  if (actualDayFreeHours <= 0) {
    return []
  }

  // 3. Shift Window from ShiftMaster (fallback to 09:00 - 18:00 if unassigned)
  let shiftStartMin = parseTimeToMinutes(emp.shift_intime)
  let shiftEndMin = parseTimeToMinutes(emp.shift_outtime)

  if (shiftStartMin == null || shiftEndMin == null || shiftEndMin <= shiftStartMin) {
    shiftStartMin = 9 * 60 // 09:00 AM
    shiftEndMin = shiftStartMin + Math.round((baseHours + 1) * 60) // e.g. 17:00 or 18:00
  }

  // Bound to the timeline visible range if applicable
  shiftStartMin = Math.max(startHour * 60, shiftStartMin)
  shiftEndMin = Math.min(endHour * 60, shiftEndMin)

  if (shiftEndMin <= shiftStartMin) return []

  let freeIntervals = [{ start: shiftStartMin, end: shiftEndMin }]

  // 4. Carve out existing jobs on this day
  const busyIntervals = (dayJobs || []).map((job) => {
    if (!job.planned_start) return null
    const s = parseNaiveDate(job.planned_start)
    const e = job.planned_end ? parseNaiveDate(job.planned_end) : new Date(s.getTime() + 60 * 60 * 1000)
    if (isNaN(s.getTime()) || isNaN(e.getTime())) return null

    const sDate = toDateKey(s)
    const eDate = toDateKey(e)
    if (eDate < dateStr || sDate > dateStr) return null

    let sMin = sDate === dateStr ? (s.getHours() * 60 + s.getMinutes()) : shiftStartMin
    let eMin = eDate === dateStr ? (e.getHours() * 60 + e.getMinutes()) : shiftEndMin

    // Date-only jobs (00:00:00) align with shift start
    if (s.getHours() === 0 && s.getMinutes() === 0 && e.getHours() === 0 && e.getMinutes() === 0) {
      const ph = parseFloat(job.planned_hours) || 1
      sMin = shiftStartMin
      eMin = Math.min(shiftEndMin, sMin + Math.round(ph * 60))
    }

    const clampedS = Math.max(shiftStartMin, sMin)
    const clampedE = Math.min(shiftEndMin, eMin <= sMin ? sMin + 30 : eMin)

    if (clampedE <= clampedS) return null
    return { start: clampedS, end: clampedE }
  }).filter(Boolean)

  busyIntervals.sort((a, b) => a.start - b.start)

  for (const busy of busyIntervals) {
    const nextFree = []
    for (const free of freeIntervals) {
      if (busy.start >= free.end || busy.end <= free.start) {
        nextFree.push(free)
      } else {
        if (busy.start > free.start && (busy.start - free.start) >= 30) {
          nextFree.push({ start: free.start, end: busy.start })
        }
        if (busy.end < free.end && (free.end - busy.end) >= 30) {
          nextFree.push({ start: busy.end, end: free.end })
        }
      }
    }
    freeIntervals = nextFree
  }

  // 5. Total free clock hours across all intervals
  const totalFreeClockHours = freeIntervals.reduce((sum, f) => sum + (f.end - f.start) / 60, 0)
  if (totalFreeClockHours <= 0) return []

  // Capacity to distribute is bounded by both available clock time and actual remaining productive capacity
  const effectiveFreeHours = Math.min(totalFreeClockHours, actualDayFreeHours)
  if (effectiveFreeHours <= 0) return []

  return freeIntervals.map((free, idx) => {
    const startH = Math.floor(free.start / 60)
    const startM = free.start % 60
    const endH = Math.floor(free.end / 60)
    const endM = free.end % 60
    const startIso = `${dateStr}T${toHHMM(startH, startM)}:00`
    const endIso = `${dateStr}T${toHHMM(endH, endM)}:00`

    const intervalDuration = (free.end - free.start) / 60
    let slotFreeHours = freeIntervals.length === 1
      ? Math.min(intervalDuration, effectiveFreeHours)
      : Math.min(intervalDuration, (intervalDuration / totalFreeClockHours) * effectiveFreeHours)
    slotFreeHours = Math.round(slotFreeHours * 10) / 10

    return {
      kind: 'slot',
      id: `slot-${emp.employee_id}-${idx}`,
      planned_start: startIso,
      planned_end: endIso,
      start: parseDate(startIso),
      end: parseDate(endIso),
      startTime: toHHMM(startH, startM),
      endTime: toHHMM(endH, endM),
      free_hours: slotFreeHours,
      shift_name: emp.shift_name || null,
    }
  }).filter((slot) => slot.free_hours > 0)
}

